import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { getRoles } from '../services/admin_service';
import { useAuth, useAdminAccess, useUserRole } from '../contexts/auth_context';
import { handleApiError } from '../utils/error_handling';

const MAX_POPOVER_WIDTH = 420;

const inputStyle = {
	width: '100%',
	padding: '9px 12px',
	background: 'var(--app-input-bg)',
	border: '1px solid var(--app-border)',
	borderRadius: 8,
	fontSize: 13,
	color: 'var(--app-text)',
	fontFamily: 'inherit',
	boxSizing: 'border-box',
	outline: 'none',
	transition: 'border-color 0.15s',
	marginBottom: 10,
};

const AdminViewComponent = ({ onCreateUser, asPopover = false, error }) => {
	const { isAuthenticated, isLoadingAuth } = useAuth();
	const { hasAdminAccess, isLoadingAccess } = useAdminAccess();
	const { userRole } = useUserRole();
	const [email, setEmail] = useState('');
	const [role, setRole] = useState('');
	const [roles, setRoles] = useState([]);

	useEffect(() => {
		const fetchRoles = async () => {
			try {
				const response = await getRoles();
				setRoles(response.roles || []);
			} catch (err) {
				handleApiError(err, { context: 'fetch_roles' });
				setRoles([]);
			}
		};
		fetchRoles();
	}, []);

	const handleSubmit = async e => {
		e.preventDefault();
		await onCreateUser(email, role);
	};

	if (isLoadingAuth || isLoadingAccess || hasAdminAccess === null) {
		return (
			<div className='flex items-center justify-center p-6'>
				<div
					className='w-6 h-6 rounded-full border-2 animate-spin'
					style={{ borderColor: 'var(--app-border)', borderTopColor: '#FF0B78' }}
				/>
			</div>
		);
	}
	if (!isAuthenticated || !hasAdminAccess) return <Navigate to='/' replace />;

	const outerClass = asPopover
		? 'p-5'
		: 'min-h-screen flex flex-col items-center justify-center p-6';

	return (
		<div className={outerClass}>
			<main id='main-content' className={asPopover ? '' : 'w-full max-w-md'} role='main'>
				{!asPopover && (
					<h2 className='text-[18px] font-bold mb-5' style={{ color: 'var(--app-text)' }}>
						Add User
					</h2>
				)}

				{error && (
					<div
						className='text-[12px] font-medium px-3 py-2 rounded-lg mb-3'
						style={{
							background: 'rgba(255,11,120,0.08)',
							border: '1px solid rgba(255,11,120,0.25)',
							color: '#FF0B78',
						}}
						role='alert'
					>
						{error}
					</div>
				)}

				<form
					onSubmit={handleSubmit}
					aria-label='Send Activation Email Form'
					style={{ width: '100%', maxWidth: asPopover ? MAX_POPOVER_WIDTH : 400 }}
				>
					<input
						id='email-input'
						type='email'
						placeholder='Email address'
						value={email}
						onChange={e => setEmail(e.target.value)}
						style={inputStyle}
						required
						aria-label='Email'
						onFocus={e => (e.target.style.borderColor = '#FF0B78')}
						onBlur={e => (e.target.style.borderColor = 'var(--app-border)')}
					/>

					<select
						id='role-input'
						value={role}
						onChange={e => setRole(e.target.value)}
						style={{ ...inputStyle, cursor: 'pointer', appearance: 'none' }}
						required
						aria-label='Role'
						onFocus={e => (e.target.style.borderColor = '#FF0B78')}
						onBlur={e => (e.target.style.borderColor = 'var(--app-border)')}
					>
						<option value='' disabled>
							Select role
						</option>
						{roles
							.filter(r => {
								const name = r.name || r;
								if (userRole === 'superuser') return true;
								if (userRole === 'admin')
									return name === 'consultant' || name === 'client';
								if (userRole === 'consultant') return name === 'client';
								return false;
							})
							.map(r => (
								<option key={r.id || r.name} value={r.name}>
									{r.name}
								</option>
							))}
					</select>

					<button
						type='submit'
						className='w-full py-2.5 rounded-lg text-[13px] font-semibold text-white transition-opacity hover:opacity-80 mt-1'
						style={{
							background: 'linear-gradient(90deg, #FF0B78, #E300C9)',
							border: 'none',
							cursor: 'pointer',
						}}
					>
						Send Activation Email
					</button>
				</form>
			</main>
		</div>
	);
};

AdminViewComponent.displayName = 'AdminView';
AdminViewComponent.propTypes = {
	onCreateUser: PropTypes.func.isRequired,
	asPopover: PropTypes.bool,
	error: PropTypes.string,
};

export const AdminView = React.memo(AdminViewComponent);
